import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, map, of, tap } from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthService } from "../auth/auth.service";
import { BaseCart, CartAttributes, RemoveCartItem, ResponseMessage } from "@models/cart.model";
import { CartDetails, ProductAttributes, SingleBlockInfo } from "@models/product.model";
import { LocalStorageService } from "../local-storage/local-storage.service";

@Injectable({
    providedIn: "root",
})
export class CartItemService {
    public cartItemDeleted = new EventEmitter<number[]>();
    public itemsCart: CartAttributes[] = [];

    private cartSubject = new BehaviorSubject<CartAttributes[]>([]);
    public cartItems$ = this.cartSubject.asObservable();

    public totalMRP: number = 0;
    public totalDiscount: number = 0;
    public subTotal: number = 0;
    private cartSyncCompleteSubject = new Subject<boolean>();
    public cartSyncComplete$ = this.cartSyncCompleteSubject.asObservable();

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
    ) {}

    /** Add Items To Cart **/
    public addToCart(item: BaseCart, product?: ProductAttributes): Observable<string> {
        if (this.authService.isUserLoggedIn) {
            return this.http.post<string>(`${environment.apiUrlBasePath}/carts/add`, item).pipe(
                tap((response) => {
                    if (response) {
                        this.addItemToLocalCart(item, product);
                    }
                }),
            );
        }
        this.addItemToLocalCart(item, product);
        return of(ResponseMessage.AddSuccess);
    }

    /** Add item to local cart and local storage **/
    private addItemToLocalCart(item: BaseCart, product?: ProductAttributes): void {
        this.localStorageService.addItemToCart(item);
        const newCartItem: CartAttributes = {
            productID: item.productID,
            cartUUID: item.cartUUID,
            quantity: item.quantity,
            product: product,
            singleBlockInfo: item.singleBlockInfo,
        };
        this.itemsCart = [newCartItem, ...this.itemsCart];
        this.cartSubject.next(this.itemsCart);
    }

    public getCartItems(): Observable<CartAttributes[]> {
        if (this.authService.isUserLoggedIn) {
            const response = this.http.get<CartAttributes[]>(`${environment.apiUrlBasePath}/carts/getCartItems`);
            return response;
        } else {
            const cartItems = this.localStorageService.getCartItems();
            if (!cartItems.length) {
                return of([]);
            }
            const cartItemsIds = cartItems.map((item) => item.productID);

            return this.http
                .post<ProductAttributes[]>(`${environment.apiUrlBasePath}/products/getProductsByIds`, cartItemsIds)
                .pipe(
                    map((products) => {
                        // Map products to cart items
                        this.itemsCart = cartItems.map((item) => ({
                            productID: item.productID,
                            cartUUID: item.cartUUID,
                            quantity: item.quantity,
                            product: products.find((product: { id: number }) => product.id === item.productID), // Find corresponding product
                            singleBlockInfo: item.singleBlockInfo,
                        }));

                        this.cartSubject.next(this.itemsCart);
                        return this.itemsCart;
                    }),
                );
        }
    }

    public updateCartQuantity(productId: number, quantity: number, cartUUID?: string): Observable<string> {
        if (this.authService.isUserLoggedIn) {
            const body = { productId, cartUUID, quantity };
            return this.http.put<string>(`${environment.apiUrlBasePath}/carts/update`, body).pipe(
                tap((response) => {
                    if (response) {
                        this.updateCartItemQuantity(productId, quantity, cartUUID);
                    }
                }),
            );
        } else {
            this.updateCartItemQuantity(productId, quantity, cartUUID);
            return of(ResponseMessage.UpdateSuccess);
        }
    }

    /** Update the Quantity Cart Item **/
    private updateCartItemQuantity(productId: number, quantity: number, cartUUID?: string): void {
        this.itemsCart = this.itemsCart.map((item) => {
            if (item.product?.id === productId) {
                return { ...item, quantity: quantity }; // return a new object with updated quantity
            }
            return item;
        });
        this.cartSubject.next(this.itemsCart);
        this.localStorageService.updateCartQuantity(productId, quantity, cartUUID!);
    }

    public removeCartItem(items: RemoveCartItem[]): Observable<string> {
        const body = items;
        if (this.authService.isUserLoggedIn) {
            return this.http.post<string>(`${environment.apiUrlBasePath}/carts/delete`, body).pipe(
                tap((response) => {
                    if (response) {
                        this.updateRemoveCartItemFromLocal(items);
                    }
                }),
            );
        } else {
            this.updateRemoveCartItemFromLocal(items);
            return of(ResponseMessage.Delete);
        }
    }

    /** Remove Cart Item  **/
    private updateRemoveCartItemFromLocal(items: RemoveCartItem[]): void {
        const productIds = items.map((item) => item.productId!);
        const cartUUIds = items.map((item) => item.cartUUID!);
        this.cartItemDeleted.emit(productIds);

        this.itemsCart = this.itemsCart.filter((cartItem) => {
            if (cartItem.cartUUID && cartUUIds.length) {
                return !cartUUIds.includes(cartItem.cartUUID);
            }

            return !productIds.includes(cartItem.productID);
        });
        this.cartSubject.next(this.itemsCart);
        this.localStorageService.removeCartItem(productIds, cartUUIds);
    }

    public removeAllCartItems(): Observable<string> {
        this.clearCartAfterOrder();
        if (this.authService.isUserLoggedIn) {
            return this.http.delete(`${environment.apiUrlBasePath}/carts/removeAll`) as Observable<string>;
        } else {
            return of(ResponseMessage.Delete);
        }
    }

    public processCartItems(justLogin: boolean): void {
        const oldCartItems = this.itemsCart;
        this.getCartItems().subscribe((savedItems) => {
            if (!oldCartItems.length) {
                // No items in local storage
                if (savedItems.length) {
                    const updateItem = savedItems.map(this.mapToCartItem); // To create a type to set in local storage
                    this.itemsCart = savedItems;
                    this.cartSubject.next(this.itemsCart);
                    this.localStorageService.setCartItems(updateItem);
                } else {
                    this.localStorageService.removeAllCartItems(); // To remove when there is no items in cart as well as local storage
                }
            } else {
                // Sync local storage
                const updatedItems = savedItems.map(this.mapToCartItem); // To create a type to set in local storage
                this.localStorageService.setCartItems(updatedItems);
                this.itemsCart = savedItems;
                this.processItems(oldCartItems, updatedItems, justLogin);
            }
        });
    }

    public mapToCartItem(item: CartAttributes): CartAttributes {
        return {
            productID: item.product?.id!,
            cartUUID: item.cartUUID,
            quantity: item.quantity,
            singleBlockInfo: item.singleBlockInfo,
        };
    }

    private processItems(oldItems: CartAttributes[], updatedItems: CartAttributes[], justLogin: boolean): void {
        const [ordinaryItems, singleBlockItems] = this.partitionItems(oldItems);
        this.processOrdinaryItems(ordinaryItems, updatedItems, justLogin); // Ordinary product
        this.processSingleBlockItems(singleBlockItems, updatedItems, justLogin); // singleBlock item
    }

    private partitionItems(items: CartAttributes[]): [CartAttributes[], CartAttributes[]] {
        return [items.filter((item) => !item.singleBlockInfo), items.filter((item) => item.singleBlockInfo)];
    }

    // Ordinary product method
    private processOrdinaryItems(
        ordinaryItems: CartAttributes[],
        updatedItems: CartAttributes[],
        justLogin: boolean,
    ): void {
        ordinaryItems.forEach((oldItem) => {
            const existingItem = updatedItems.find((item) => item.productID === oldItem.productID);
            if (existingItem) {
                if (existingItem.quantity !== oldItem.quantity && !existingItem.singleBlockInfo) {
                    this.updateCartQuantity(oldItem.productID!, oldItem.quantity, oldItem.cartUUID!).subscribe();
                }
            } else {
                if (justLogin) {
                    this.addToCart(oldItem, oldItem.product).subscribe((res) => {
                        if (res) {
                            this.cartSyncCompleteSubject.next(true);
                        }
                    });
                }
            }
        });
    }

    //SingleBlock Item method
    private processSingleBlockItems(
        singleBlockItems: CartAttributes[],
        updatedItems: CartAttributes[],
        justLogin: boolean,
    ): void {
        const updatedBlockItems = updatedItems.filter((item) => item.singleBlockInfo);
        singleBlockItems.forEach((item) => {
            const exists = updatedBlockItems.some((ele) => ele.cartUUID === item.cartUUID);
            if (!exists && justLogin) {
                this.addToCart(item, item.product).subscribe((res) => {
                    if (res) {
                        this.cartSyncCompleteSubject.next(true);
                    }
                });
            }
        });
    }

    public calculateTotalMRP(items: CartAttributes[]): void {
        this.totalMRP = 0;
        this.totalDiscount = 0;
        this.subTotal = 0;
        for (let item of items) {
            const quantity = item.quantity;
            const productTotalPrice = item.product!.price * quantity;
            const productTotalDiscount =
                (item.product!.discountedPrice ? item.product!.price - item.product!.discountedPrice : 0) * quantity;
            this.totalMRP += productTotalPrice;
            this.totalDiscount += productTotalDiscount;
            this.subTotal = this.totalMRP - this.totalDiscount;
        }
    }

    public getFacesByType(data: SingleBlockInfo, type: string): string[] {
        const faceArray = [];
        for (const [key, value] of Object.entries(data)) {
            if (value === type) {
                faceArray.push(key.charAt(0).toUpperCase() + key.slice(1).toLowerCase());
            }
        }
        return faceArray;
    }

    /*** Reset itemsCart after logout ***/
    public resetCartItems(): void {
        this.itemsCart = [];
        this.cartSubject.next(this.itemsCart);
    }

    public clearCartAfterOrder(): void {
        this.itemsCart = this.itemsCart.filter((item) => !item.product?.isActive);
        if (this.itemsCart.length) {
            this.localStorageService.setCartItems(this.itemsCart);
        } else {
            this.localStorageService.removeAllCartItems();
        }
    }
}
